import styled, { css } from 'styled-components';
import rem from 'polished/lib/helpers/rem';
// template imports
import Col from '@nmx/template/dist/componentLibrary/foundations/Grid/Col';
import Container from '@nmx/template/dist/componentLibrary/foundations/Grid/Container';
import Row from '@nmx/template/dist/componentLibrary/foundations/Grid/Row';
// NM 2024 theme
import Nm2024Theme from '../../../../nm2024Theme/nm2024Theme/colors';
// nm2024 themed components
import { LinkNm2024 } from '../../../../nm2024Theme/components/link/styles';
import { TypographyNm2024 } from '../../../../nm2024Theme/components/typography/styles';
// images
import heroImageDesktopWebp from './images/home-hero-desktop.webp';
import heroImageMobileWebp from './images/home-hero-mobile.webp';
// low res images
import { lowResImageDesktop, lowResImageMobile } from './lowResImages';

export const ColStyled = styled(Col)` 
  ${({ theme }) => {
    const xlargeUp = theme.xlargeUp();
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    const smallDown = theme.smallDown();

    let thisPaddingTop = rem(20);
    let thisPaddingBottom = rem(40);
    let thisMargin = 'auto';

    if (largeUp) {
      thisPaddingTop = rem(56);
      thisPaddingBottom = rem(56);
    } else if (mediumUp) {
      thisPaddingTop = rem(35);
      thisPaddingBottom = rem(35);
    }

    if(xlargeUp){
      thisMargin = 'auto 0';
    }else if(mediumUp){
      thisMargin = 'auto 0 auto 5%';
    }

    return css`
      position: relative;
      height: auto;
      max-width: ${mediumUp ? '53%' : '100%'};
      margin: ${thisMargin};
      padding-top: ${thisPaddingTop};
      padding-right:  ${mediumUp ? '5%' : rem(16)};
      padding-bottom: ${thisPaddingBottom};
      padding-left: ${mediumUp ? '5%' : rem(16)};
      text-align: ${mediumUp ? 'left' : 'center'};
      background-color: ${Nm2024Theme.navy.navy};
      ${smallDown && 'background-size: 120%;'}
      @media print {
        && {
          max-width: 100% !important;
          flex: 100% !important;
        }
      }
    `;
  }}
`;

export const ContainerStyled = styled(Container)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    let marginTop = rem(170);
    let marginBottom = rem(30);
    let horizontalPadding = rem(15);

    if(mediumUp){
      marginTop = 'auto';
      marginBottom = 0;
      horizontalPadding = 0;
    }

    return css`
      && {
        height: 100%;
        margin-top: ${marginTop};
        margin-bottom: ${marginBottom};
        padding-right: ${horizontalPadding};
        padding-left: ${horizontalPadding};
      }
    `;
  }}
`;

export const DivStyledBackgroundContainer = styled.div`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    const smallToMedium = theme.smallToMedium();
    let thisBgPositionY = 'top';
    let thisBgPositionX = 'center';
    const thisScale = 1;

    // horizontal adjustment
    if(mediumUp){
      thisBgPositionX = '65%';
    }

    // vertical adjustment
    if(smallToMedium){
      thisBgPositionY = '5%';
    }

    return css`
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-color: ${theme.colors.neutral.gray.gray};
      background-image: url(${mediumUp ? lowResImageDesktop : lowResImageMobile});
      background-image: url(${mediumUp ? heroImageDesktopWebp : heroImageMobileWebp});
      background-position-x: ${thisBgPositionX};
      background-position-y: ${thisBgPositionY};
      background-size: cover;
      background-repeat: no-repeat;
      z-index: -1;
      -webkit-animation: sharpenBg 1s both;
      animation: sharpenBg 1s both;
      transform: scale(${thisScale});
      @keyframes sharpenBg {
        0% {
          -webkit-filter: blur(${rem(20)});
          filter: blur(${rem(20)});
        }
        100% {
          -webkit-filter: blur(0);
          filter: blur(0);
        }
      }
      @media print {
        && {
          display: none !important;
        }
      }
    `;
  }}
`;

export const LinkStyledButton = styled(LinkNm2024)`
  ${({ theme }) => {
    const largeUp = theme.largeUp();
    const smallUp = theme.smallUp();
    return css`
      width: 100%;
      max-width: none;
      margin: ${rem(20)} auto 0;
      ${smallUp && 'width: auto;'};
      ${largeUp && `margin-top: ${rem(20)};`}
    `;
  }
}`;

export const RowStyled = styled(Row)`
  height: 100%;
`;

export const SectionStyled = styled.section`
  ${({ theme }) => {
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();

    let thisHeight = 'auto';

    if (largeUp) {
      thisHeight = rem(550);
    } else if (mediumUp) {
      thisHeight = rem(440);
    }

    return css`
      position: relative;
      height: ${thisHeight};
      padding-top: 0; /** for bg blur-up // overwrite global section padding */
      padding-bottom: 0; /** for bg blur-up // overwrite global section padding */
      overflow: hidden; /** for bg blur-up */

      @media print {
        && {
          background: none !important;
        }
      }
    `;
  }}
`;

// "welcome to"
export const TypographyStyledHighlight = styled(TypographyNm2024)`
  ${({ theme }) => {
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();

    let thisFontSize = rem(37);
    let thisLineHeight = rem(37);

    if(largeUp){
      thisFontSize = rem(46);
      thisLineHeight = rem(40);
    }else if (mediumUp) {
      thisFontSize = rem(33);
      thisLineHeight = rem(33);
    }

    return css`
      && {
        color: ${theme.colors.neutral.white};
        font-size: ${thisFontSize};
        line-height: ${thisLineHeight};
        padding-bottom: 0;
        text-transform: none;
      }
    `;
  }}
`;

// "A better way to money"
export const TypographyStyledHeading = styled(TypographyNm2024)`
  ${({ theme }) => {
    const largeUp = theme.largeUp();
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();

    let thisWidth = '100%';
    let thisMarginBottom = rem(12);
    let thisFontSize = rem(40);
    let thisLineHeight = rem(44);
    let thisTextAlign = 'center';
    const thisHorizontalPadding = mediumUp ? 0 : rem(12);

    if(mediumUp){
      thisTextAlign = 'left';
      thisMarginBottom = rem(10);
    }

    if(largeUp){
      thisFontSize = rem(56);
      thisLineHeight = rem(56);
    }else if(mediumUp){
      thisFontSize = rem(48);
      thisLineHeight = rem(44);
    }

    if(smallUp && !mediumUp){
      thisWidth = '85%';
    }

    return css`
      && {
        width: ${thisWidth};
        max-width: ${thisWidth};
        margin: 0 auto ${thisMarginBottom};
        padding-right: ${thisHorizontalPadding};
        padding-left: ${thisHorizontalPadding};
        font-size: ${thisFontSize};
        line-height: ${thisLineHeight};
        font-style: normal;
        font-display: swap;
        font-weight: 500;
        letter-spacing: 0;
        color: ${Nm2024Theme.sky.sky};
        text-align: ${thisTextAlign};
        text-transform: uppercase;
        text-shadow: none;
        ::before {
          box-shadow: none;
        }
      }
    `;
  }}
`;

export const TypographyStyledSubheading = styled(TypographyNm2024)`
  ${({ theme }) => {
    const mediumUp = theme.mediumUp();
    const smallUp = theme.smallUp();
    let thisWidth = '100%';

    if (smallUp && !mediumUp) {
      thisWidth = '85%';
    }

    return css`
      && {
        display: block;
        width: ${thisWidth};
        max-width: ${thisWidth};
        margin: 0 auto;
        color: ${theme.colors.neutral.white};
        text-align: ${mediumUp ? 'left' : 'center'};
      }
    `;
  }}
`;

